import { useContext, useRef } from 'react';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

import type { FlagKey } from '../registry';

import type { BucketBy } from './FeatureFlagContext';
import { FeatureFlagContext } from './FeatureFlagContext';
import { shallowEqual } from './shallowEqual';

export interface UseFeatureFlagOptions {
  /** Bucketing mode: 'user' uses userId (cross-device consistent),
   *  'device' uses deviceId (stable across login/logout). */
  bucketBy: BucketBy;
  /** Optional attributes for Flipt targeting rules (e.g., { email: 'user@zep.us' }).
   *  Inline object literals are safe — the hook stabilizes the reference internally. */
  context?: Record<string, string>;
}

/**
 * Hook to evaluate a single boolean feature flag.
 * Returns the enabled state and whether the provider is ready.
 *
 * @param flagKey  - The flag key to evaluate
 * @param options  - bucketBy (required) + optional targeting context
 *
 * @example
 * ```ts
 * // User bucketing (cross-device consistent)
 * const { enabled } = useFeatureFlag('new-feature', { bucketBy: 'user' });
 *
 * // Device bucketing (stable across login/logout)
 * const { enabled } = useFeatureFlag('signup-test', { bucketBy: 'device' });
 * ```
 */
export function useFeatureFlag(
  flagKey: FlagKey,
  options: UseFeatureFlagOptions,
): {
  enabled: boolean;
  isReady: boolean;
} {
  const { userEntityId, deviceEntityId, ssrFlags, isReady } =
    useContext(FeatureFlagContext);

  const effectiveEntityId =
    options.bucketBy === 'device' ? deviceEntityId : userEntityId;

  // Stabilize reference so useFliptBoolean skips WASM re-evaluation
  // when values haven't actually changed (inline object literals are safe).
  const stableRef = useRef(options.context);
  if (!shallowEqual(stableRef.current, options.context)) {
    stableRef.current = options.context;
  }

  const defaultValue = ssrFlags?.[flagKey] ?? false;
  const enabled = useFliptBoolean(
    flagKey,
    defaultValue,
    effectiveEntityId,
    stableRef.current,
  );

  return { enabled, isReady };
}
